import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { timeAgo } from "@/lib/timeAgo";
import { eventIcon, eventIconColor } from "@/lib/notificationTypes";
import { Bell } from "@phosphor-icons/react";
import { toast } from "sonner";

const PREVIEW_LIMIT = 5;

// Header notification bell — a lightweight PREVIEW (latest 5) over the same /api/notifications
// primitive that powers the full Notification Center at /app/notifications (Notifications.jsx).
// Any future event type just needs an entry in lib/notificationTypes.js to render correctly here.
export default function NotificationBell() {
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    async function loadUnreadCount() {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnreadCount(data?.unread_count || 0);
        } catch { /* silent — non-critical UI badge */ }
    }

    async function loadList() {
        try {
            const { data } = await api.get("/notifications", { params: { limit: PREVIEW_LIMIT } });
            setItems(data?.items || []);
        } catch (e) { toast.error(formatError(e)); }
    }

    useEffect(() => {
        loadUnreadCount();
        // Deliberately not aggressive — the header badge only needs to be roughly fresh; opening
        // the dropdown (or the full Notification Center) always fetches live data.
        const interval = setInterval(loadUnreadCount, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (open) loadList();
    }, [open]);

    async function handleClick(n) {
        if (!n.read) {
            try {
                await api.post(`/notifications/${n.id}/read`);
                setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
                setUnreadCount((c) => Math.max(0, c - 1));
            } catch { /* non-critical */ }
        }
        setOpen(false);
        if (n.deep_link) nav(n.deep_link);
    }

    function viewAll() {
        setOpen(false);
        nav("/app/notifications");
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button data-testid="notification-bell-btn" className="relative p-2 rounded-full hover:bg-brand-bg transition-colors">
                    <Bell size={18} className="text-brand-ink" />
                    {unreadCount > 0 && (
                        <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand-action text-white text-[10px] font-semibold flex items-center justify-center">
                            {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-sm p-0 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notification-dropdown">
                <div className="px-4 py-3 border-b border-brand-line flex items-center justify-between">
                    <p className="font-heading text-sm text-brand-ink">Notifications</p>
                </div>
                <div className="overflow-y-auto flex-1">
                    {items.length === 0 ? (
                        <p data-testid="notification-dropdown-empty" className="text-sm text-brand-inkSoft text-center py-8">No notifications yet</p>
                    ) : (
                        items.map((n) => {
                            const Icon = eventIcon(n.event_type);
                            return (
                                <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => handleClick(n)}
                                    className={`w-full text-left px-4 py-3 border-b border-brand-line/60 last:border-0 hover:bg-brand-bg/60 transition-colors flex gap-2.5 items-start ${!n.read ? "bg-brand-sage/20" : ""}`}>
                                    <Icon size={16} weight="duotone" className={`mt-0.5 shrink-0 ${eventIconColor(n.event_type)}`} />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-brand-ink">{n.title}</p>
                                        <p className="text-xs text-brand-inkSoft mt-0.5">{n.message}</p>
                                        <p className="text-[10px] text-brand-inkSoft/70 mt-1">{timeAgo(n.created_at)}</p>
                                    </div>
                                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-brand-action shrink-0 mt-1.5" />}
                                </button>
                            );
                        })
                    )}
                </div>
                <button data-testid="notification-view-all-btn" onClick={viewAll}
                    className="px-4 py-2.5 text-center text-xs uppercase tracking-overline text-brand-action font-medium border-t border-brand-line hover:bg-brand-bg transition-colors shrink-0">
                    View all notifications
                </button>
            </PopoverContent>
        </Popover>
    );
}
